/**
 * Text-only segmented control (Voluum-style path radios).
 * For Media Devoted | Created By Ciro Autiero
 */

import { Fragment } from 'react';

import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import type { PathSettingPillOption } from './path-settings.constants';

export interface PillSegmentRowProps<T extends string> {
  value: T;
  options: PathSettingPillOption<T>[];
  onChange: (v: T) => void;
  'aria-label': string;
  compact?: boolean;
}

export default function PillSegmentRow<T extends string>({
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
  compact = false,
}: PillSegmentRowProps<T>) {
  return (
    <div
      className={cn('flex flex-wrap', compact ? 'gap-1.5' : 'gap-2')}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const control = (
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-full border text-left font-semibold transition-colors',
              compact
                ? 'px-2 py-1 text-[11px]'
                : 'px-3 py-1.5 text-xs sm:text-sm',
              selected
                ? 'border-primary bg-primary/10 text-foreground shadow-sm ring-2 ring-primary/25'
                : 'border-border bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
        return opt.description ? (
          <Tooltip key={opt.value} content={opt.description} side="top">
            {control}
          </Tooltip>
        ) : (
          <Fragment key={opt.value}>{control}</Fragment>
        );
      })}
    </div>
  );
}
